/**
 * As escalas ilustradas do pacote clínico.
 *
 * **Dados, não desenho.** A arte vive em `assets/clinical/escalas/` e é a fonte:
 * a grade, o peso de traço e o padding são comuns ao acervo inteiro, e um
 * desenho avulso quebraria a leitura de todas. Este arquivo diz quais opções
 * existem, o que cada uma quer dizer e onde está a imagem — nada mais.
 *
 * Três escalas têm arte incompleta (`semArte`). Elas continuam no catálogo,
 * porque a escala existe e o profissional a conhece pelo nome; o que falta é a
 * ilustração, e a tela diz isso em vez de mostrar quadrado vazio. Esconder a
 * escala seria pior: quem procura Norwood não descobriria que ela está aqui.
 *
 * Portado de `ui_kits/vecchia_app/fichasCatalogo.js`, que continua sendo o
 * catálogo do protótipo. Os `valor` são a chave da resposta — nunca mudam.
 */

import { urlDaArteClinica } from './arteClinica';

export interface OpcaoDaEscala {
    /** A chave gravada na resposta. Nunca muda: prontuário antigo depende dela. */
    valor: string;
    rotulo: string;
    /** O que o leitor de tela anuncia no lugar da ilustração. */
    alt: string;
    /** O critério clínico, quando a escala tem um. */
    dica?: string;
    /** Caminho dentro de `assets/clinical/escalas/`. Ausente = sem arte ainda. */
    imagem?: string;
    /** Escala de tom (Fitzpatrick, cor de urina): a cor É a ilustração. */
    cor?: string;
}

export interface EscalaIlustrada {
    slug: string;
    nome: string;
    descricao: string;
    /** Quantas colunas a grade usa — vem do acervo, não do palpite da tela. */
    colunas: number;
    /** Quantas opções ainda esperam ilustração. */
    semArte?: number;
    opcoes: OpcaoDaEscala[];
}

export const ESCALAS_ILUSTRADAS: EscalaIlustrada[] = [
    {
        slug: 'tipo-de-cabelo',
        nome: 'Tipo de cabelo',
        descricao: 'Avalie o fio seco, sem produto, na região da nuca.',
        colunas: 3,
        opcoes: [
            { valor: 'liso', rotulo: 'Liso', alt: 'Liso — ilustração da escala', imagem: 'tipo-de-cabelo/liso.jpg' },
            { valor: 'ondulados', rotulo: 'Ondulados', alt: 'Ondulados — ilustração da escala', imagem: 'tipo-de-cabelo/ondulados.jpg' },
            { valor: 'crespo', rotulo: 'Crespo', alt: 'Crespo — ilustração da escala', imagem: 'tipo-de-cabelo/crespo.jpg' },
        ],
    },
    {
        slug: 'alopecia-areata',
        nome: 'Alopecia areata',
        descricao: 'O enquadramento precisa mostrar a distribuição, não só a placa.',
        colunas: 4,
        opcoes: [
            { valor: 'unifocal', rotulo: 'Unifocal', alt: 'Unifocal — ilustração da escala', imagem: 'alopecia-areata/unifocal.png' },
            { valor: 'multifocal', rotulo: 'Multifocal', alt: 'Multifocal — ilustração da escala', imagem: 'alopecia-areata/multifocal.png' },
            { valor: 'difusa', rotulo: 'Difusa', alt: 'Difusa — ilustração da escala', imagem: 'alopecia-areata/difusa.png' },
            { valor: 'ofiasica', rotulo: 'Ofiásica', alt: 'Ofiásica — ilustração da escala', imagem: 'alopecia-areata/ofiasica.png' },
            { valor: 'reticular', rotulo: 'Reticular', alt: 'Reticular — ilustração da escala', imagem: 'alopecia-areata/reticular.png' },
            { valor: 'total', rotulo: 'Total', alt: 'Total — ilustração da escala', imagem: 'alopecia-areata/total.png' },
            { valor: 'universal', rotulo: 'Universal', alt: 'Universal — ilustração da escala' },
        ],
    },
    {
        slug: 'displasias-congenitas',
        nome: 'Displasias pilosas congênitas',
        descricao: 'Alterações da haste presentes desde o nascimento.',
        colunas: 5,
        opcoes: [
            { valor: 'moniletrix', rotulo: 'Moniletrix', alt: 'Moniletrix — ilustração da escala', imagem: 'displasias-congenitas/moniletrix.png' },
            { valor: 'tricorrexe-invaginada', rotulo: 'Tricorrexe invaginada', alt: 'Tricorrexe invaginada — ilustração da escala', imagem: 'displasias-congenitas/tricorrexe-invaginada.png' },
            { valor: 'tricopoliodistrofia', rotulo: 'Tricopoliodistrofia', alt: 'Tricopoliodistrofia — ilustração da escala', imagem: 'displasias-congenitas/tricopoliodistrofia.png' },
            { valor: 'cabelos-anagenos-frouxos', rotulo: 'Cabelos anágenos frouxos', alt: 'Cabelos anágenos frouxos — ilustração da escala', imagem: 'displasias-congenitas/cabelos-anagenos-frouxos.png' },
            { valor: 'sindrome-dos-cabelos-impenteaveis', rotulo: 'Síndrome dos cabelos impenteáveis', alt: 'Síndrome dos cabelos impenteáveis — ilustração da escala', imagem: 'displasias-congenitas/sindrome-dos-cabelos-impenteaveis.png' },
        ],
    },
    {
        slug: 'displasias-adquiridas',
        nome: 'Displasias pilosas adquiridas',
        descricao: 'Alterações da haste por trauma ou manipulação.',
        colunas: 4,
        opcoes: [
            { valor: 'tricorrexe-nodosa', rotulo: 'Tricorrexe nodosa', alt: 'Tricorrexe nodosa — ilustração da escala', imagem: 'displasias-adquiridas/tricorrexe-nodosa.png' },
            { valor: 'tricoptilose', rotulo: 'Tricoptilose', alt: 'Tricoptilose — ilustração da escala', imagem: 'displasias-adquiridas/tricoptilose.png' },
            { valor: 'triconodose', rotulo: 'Triconodose', alt: 'Triconodose — ilustração da escala', imagem: 'displasias-adquiridas/triconodose.png' },
            { valor: 'tricotilomania', rotulo: 'Tricotilomania', alt: 'Tricotilomania — ilustração da escala', imagem: 'displasias-adquiridas/tricotilomania.png' },
        ],
    },
    {
        slug: 'escala-savin',
        nome: 'Escala de Savin',
        descricao: 'Padrão feminino. A risca central alarga em direção à frente.',
        colunas: 5,
        /* 9 das 9 opções ainda esperam a arte do acervo. */
        semArte: 9,
        opcoes: [
            { valor: 'i-1', rotulo: 'I-1', alt: 'I-1 — ilustração da escala', dica: 'Risca normal' },
            { valor: 'i-2', rotulo: 'I-2', alt: 'I-2 — ilustração da escala', dica: 'Alargamento discreto' },
            { valor: 'i-3', rotulo: 'I-3', alt: 'I-3 — ilustração da escala', dica: 'Alargamento moderado' },
            { valor: 'i-4', rotulo: 'I-4', alt: 'I-4 — ilustração da escala', dica: 'Alargamento acentuado' },
            { valor: 'ii-1', rotulo: 'II-1', alt: 'II-1 — ilustração da escala', dica: 'Rarefação difusa inicial' },
            { valor: 'ii-2', rotulo: 'II-2', alt: 'II-2 — ilustração da escala', dica: 'Rarefação difusa' },
            { valor: 'iii', rotulo: 'III', alt: 'III — ilustração da escala', dica: 'Rarefação difusa do vértice' },
            { valor: 'avancado', rotulo: 'Avançado', alt: 'Avançado — ilustração da escala', dica: 'Perda extensa do vértice' },
            { valor: 'frontal', rotulo: 'Frontal', alt: 'Frontal — ilustração da escala', dica: 'Recuo da linha anterior' },
        ],
    },
    {
        slug: 'escala-norwood',
        nome: 'Escala de Norwood-Hamilton',
        descricao: 'Padrão masculino. As variantes “a” recuam em faixa, sem entradas.',
        colunas: 6,
        /* 10 das 12 opções ainda esperam a arte do acervo. */
        semArte: 10,
        opcoes: [
            { valor: 'tipo-i', rotulo: 'Tipo I', alt: 'Tipo I — ilustração da escala', dica: 'Sem recuo' },
            { valor: 'tipo-ii', rotulo: 'Tipo II', alt: 'Tipo II — ilustração da escala', dica: 'Entradas discretas' },
            { valor: 'tipo-i-a', rotulo: 'Tipo I a', alt: 'Tipo I a — ilustração da escala', dica: 'Recuo em faixa' },
            { valor: 'tipo-iii', rotulo: 'Tipo III', alt: 'Tipo III — ilustração da escala', dica: 'Entradas evidentes' },
            { valor: 'tipo-iii-a', rotulo: 'Tipo III a', alt: 'Tipo III a — ilustração da escala', dica: 'Faixa acentuada' },
            { valor: 'tipo-iii-vertex', rotulo: 'Tipo III Vértex', alt: 'Tipo III Vértex — ilustração da escala', dica: 'Entradas + vértice' },
            { valor: 'tipo-iv', rotulo: 'Tipo IV', alt: 'Tipo IV — ilustração da escala', dica: 'Vértice ampliado' },
            { valor: 'tipo-iv-a', rotulo: 'Tipo IV a', alt: 'Tipo IV a — ilustração da escala', dica: 'Faixa profunda' },
            { valor: 'tipo-v', rotulo: 'Tipo V', alt: 'Tipo V — ilustração da escala', dica: 'Ponte estreita' },
            { valor: 'tipo-v-a', rotulo: 'Tipo V a', alt: 'Tipo V a — ilustração da escala', dica: 'Faixa + vértice' },
            { valor: 'tipo-vi', rotulo: 'Tipo VI', alt: 'Tipo VI — ilustração da escala', dica: 'Ponte rompida', imagem: 'escala-norwood/tipo-vi.png' },
            { valor: 'tipo-vii', rotulo: 'Tipo VII', alt: 'Tipo VII — ilustração da escala', dica: 'Apenas a coroa', imagem: 'escala-norwood/tipo-vii.png' },
        ],
    },
    {
        slug: 'grau-celulite',
        nome: 'Grau de celulite',
        descricao: 'Fotografe com luz rasante — luz frontal apaga o relevo.',
        colunas: 4,
        opcoes: [
            { valor: 'grau-01', rotulo: 'Grau 01', alt: 'Grau 01 — ilustração da escala', imagem: 'grau-celulite/grau-01.jpg' },
            { valor: 'grau-02', rotulo: 'Grau 02', alt: 'Grau 02 — ilustração da escala', imagem: 'grau-celulite/grau-02.jpg' },
            { valor: 'grau-03', rotulo: 'Grau 03', alt: 'Grau 03 — ilustração da escala', imagem: 'grau-celulite/grau-03.jpg' },
            { valor: 'grau-04', rotulo: 'Grau 04', alt: 'Grau 04 — ilustração da escala', imagem: 'grau-celulite/grau-04.jpg' },
        ],
    },
    {
        slug: 'estrias',
        nome: 'Estrias',
        descricao: 'A cor é o critério: rubra é recente, alba é madura.',
        colunas: 2,
        opcoes: [
            { valor: 'rubra', rotulo: 'Rubra', alt: 'Rubra — ilustração da escala', dica: 'Recente, avermelhada', imagem: 'estrias/rubra.jpg' },
            { valor: 'alba', rotulo: 'Alba', alt: 'Alba — ilustração da escala', dica: 'Madura, nacarada', imagem: 'estrias/alba.jpg' },
        ],
    },
    {
        slug: 'quantificacao-estrias',
        nome: 'Quantificação de estrias',
        descricao: 'Densidade por área avaliada.',
        colunas: 4,
        /* 4 das 4 opções ainda esperam a arte do acervo. */
        semArte: 4,
        opcoes: [
            { valor: 'poucas', rotulo: 'Poucas', alt: 'Poucas — ilustração da escala', dica: 'Até 5 por área' },
            { valor: 'moderadas', rotulo: 'Moderadas', alt: 'Moderadas — ilustração da escala', dica: '6 a 15' },
            { valor: 'muitas', rotulo: 'Muitas', alt: 'Muitas — ilustração da escala', dica: '16 a 30' },
            { valor: 'confluentes', rotulo: 'Confluentes', alt: 'Confluentes — ilustração da escala', dica: 'Mais de 30' },
        ],
    },
    {
        slug: 'distribuicao-gordura',
        nome: 'Distribuição de gordura corporal',
        descricao: 'Padrão de deposição predominante.',
        colunas: 2,
        opcoes: [
            { valor: 'androide', rotulo: 'Androide', alt: 'Androide — ilustração da escala', dica: 'Predomínio abdominal', imagem: 'distribuicao-gordura/androide.svg' },
            { valor: 'ginoide', rotulo: 'Ginoide', alt: 'Ginoide — ilustração da escala', dica: 'Predomínio em quadril e coxa', imagem: 'distribuicao-gordura/ginoide.svg' },
        ],
    },
    {
        slug: 'diastase-teste',
        nome: 'Teste de diástase de reto abdominal',
        descricao: 'Palpação em decúbito dorsal com flexão de tronco.',
        colunas: 2,
        opcoes: [
            { valor: 'negativo', rotulo: 'Negativo', alt: 'Negativo — ilustração da escala', imagem: 'diastase-teste/negativo.svg' },
            { valor: 'positivo', rotulo: 'Positivo', alt: 'Positivo — ilustração da escala', imagem: 'diastase-teste/positivo.svg' },
        ],
    },
    {
        slug: 'diastase-tipo',
        nome: 'Tipo de diástase de reto abdominal',
        descricao: 'Localização do afastamento.',
        colunas: 4,
        opcoes: [
            { valor: 'tipo-a', rotulo: 'Tipo A', alt: 'Tipo A — ilustração da escala', dica: 'Supraumbilical', imagem: 'diastase-tipo/tipo-a.svg' },
            { valor: 'tipo-b', rotulo: 'Tipo B', alt: 'Tipo B — ilustração da escala', dica: 'Umbilical', imagem: 'diastase-tipo/tipo-b.svg' },
            { valor: 'tipo-c', rotulo: 'Tipo C', alt: 'Tipo C — ilustração da escala', dica: 'Infraumbilical', imagem: 'diastase-tipo/tipo-c.svg' },
            { valor: 'tipo-d', rotulo: 'Tipo D', alt: 'Tipo D — ilustração da escala', dica: 'Difusa', imagem: 'diastase-tipo/tipo-d.svg' },
        ],
    },
    {
        slug: 'cicatrizes-acne',
        nome: 'Cicatrizes de acne',
        descricao: 'Luz rasante: as três primeiras se distinguem pelo perfil da depressão.',
        colunas: 4,
        opcoes: [
            { valor: 'icepick', rotulo: 'Icepick', alt: 'Icepick — ilustração da escala', dica: 'Estreita e profunda', imagem: 'cicatrizes-acne/icepick.png' },
            { valor: 'boxcar', rotulo: 'Boxcar', alt: 'Boxcar — ilustração da escala', dica: 'Bordas verticais definidas', imagem: 'cicatrizes-acne/boxcar.png' },
            { valor: 'rolling', rotulo: 'Rolling', alt: 'Rolling — ilustração da escala', dica: 'Ondulada, bordas suaves', imagem: 'cicatrizes-acne/rolling.png' },
            { valor: 'hipertrofica', rotulo: 'Hipertrófica', alt: 'Hipertrófica — ilustração da escala', dica: 'Elevada', imagem: 'cicatrizes-acne/hipertrofica.png' },
        ],
    },
    {
        slug: 'discromia-facial',
        nome: 'Discromia facial',
        descricao: 'Confirme a nomenclatura das localizações com a clínica.',
        colunas: 3,
        opcoes: [
            { valor: 'localizacao-1', rotulo: 'Localização 1', alt: 'Localização 1 — ilustração da escala', imagem: 'discromia-facial/localizacao-1.png' },
            { valor: 'localizacao-2', rotulo: 'Localização 2', alt: 'Localização 2 — ilustração da escala', imagem: 'discromia-facial/localizacao-2.png' },
            { valor: 'localizacao-3', rotulo: 'Localização 3', alt: 'Localização 3 — ilustração da escala', imagem: 'discromia-facial/localizacao-3.png' },
        ],
    },
    {
        slug: 'hiperpigmentacao-periocular',
        nome: 'Tipo de hiperpigmentação periocular',
        descricao: 'Mesma altura de câmera nas seis, sem maquiagem.',
        colunas: 6,
        opcoes: [
            { valor: 'tipo-1', rotulo: 'Tipo 1', alt: 'Tipo 1 — ilustração da escala', imagem: 'hiperpigmentacao-periocular/tipo-1.png' },
            { valor: 'tipo-2', rotulo: 'Tipo 2', alt: 'Tipo 2 — ilustração da escala', imagem: 'hiperpigmentacao-periocular/tipo-2.png' },
            { valor: 'tipo-3', rotulo: 'Tipo 3', alt: 'Tipo 3 — ilustração da escala', imagem: 'hiperpigmentacao-periocular/tipo-3.png' },
            { valor: 'tipo-4', rotulo: 'Tipo 4', alt: 'Tipo 4 — ilustração da escala', imagem: 'hiperpigmentacao-periocular/tipo-4.png' },
            { valor: 'tipo-5', rotulo: 'Tipo 5', alt: 'Tipo 5 — ilustração da escala', imagem: 'hiperpigmentacao-periocular/tipo-5.png' },
            { valor: 'tipo-6', rotulo: 'Tipo 6', alt: 'Tipo 6 — ilustração da escala', imagem: 'hiperpigmentacao-periocular/tipo-6.png' },
        ],
    },
    {
        slug: 'formato-corporal',
        nome: 'Formato corporal',
        descricao: 'Proporção entre ombro, cintura e quadril.',
        colunas: 5,
        opcoes: [
            { valor: 'oval', rotulo: 'Oval', alt: 'Oval — ilustração da escala', imagem: 'formato-corporal/oval.svg' },
            { valor: 'retangulo', rotulo: 'Retângulo', alt: 'Retângulo — ilustração da escala', imagem: 'formato-corporal/retangulo.svg' },
            { valor: 'ampulheta', rotulo: 'Ampulheta', alt: 'Ampulheta — ilustração da escala', imagem: 'formato-corporal/ampulheta.svg' },
            { valor: 'triangulo-invertido', rotulo: 'Triângulo invertido', alt: 'Triângulo invertido — ilustração da escala', imagem: 'formato-corporal/triangulo-invertido.svg' },
            { valor: 'triangulo', rotulo: 'Triângulo', alt: 'Triângulo — ilustração da escala', imagem: 'formato-corporal/triangulo.svg' },
        ],
    },
    {
        slug: 'formato-sobrancelha',
        nome: 'Formato de sobrancelha',
        descricao: 'Desenho do arco.',
        colunas: 4,
        opcoes: [
            { valor: 'retas', rotulo: 'Retas', alt: 'Retas — ilustração da escala', imagem: 'formato-sobrancelha/retas.svg' },
            { valor: 'angulares', rotulo: 'Angulares', alt: 'Angulares — ilustração da escala', imagem: 'formato-sobrancelha/angulares.svg' },
            { valor: 'semiangulares', rotulo: 'Semiangulares', alt: 'Semiangulares — ilustração da escala', imagem: 'formato-sobrancelha/semiangulares.svg' },
            { valor: 'arqueadas', rotulo: 'Arqueadas', alt: 'Arqueadas — ilustração da escala', imagem: 'formato-sobrancelha/arqueadas.svg' },
            { valor: 'semiarqueadas', rotulo: 'Semiarqueadas', alt: 'Semiarqueadas — ilustração da escala', imagem: 'formato-sobrancelha/semiarqueadas.svg' },
            { valor: 'arredondadas', rotulo: 'Arredondadas', alt: 'Arredondadas — ilustração da escala', imagem: 'formato-sobrancelha/arredondadas.svg' },
            { valor: 'semiarredondadas', rotulo: 'Semiarredondadas', alt: 'Semiarredondadas — ilustração da escala', imagem: 'formato-sobrancelha/semiarredondadas.svg' },
        ],
    },
    {
        slug: 'formato-rosto',
        nome: 'Formatos de rosto',
        descricao: 'Proporção entre testa, maçãs e mandíbula.',
        colunas: 3,
        opcoes: [
            { valor: 'oval', rotulo: 'Oval', alt: 'Oval — ilustração da escala', imagem: 'formato-rosto/oval.svg' },
            { valor: 'redondo', rotulo: 'Redondo', alt: 'Redondo — ilustração da escala', imagem: 'formato-rosto/redondo.svg' },
            { valor: 'quadrado', rotulo: 'Quadrado', alt: 'Quadrado — ilustração da escala', imagem: 'formato-rosto/quadrado.svg' },
            { valor: 'alongado', rotulo: 'Alongado', alt: 'Alongado — ilustração da escala', imagem: 'formato-rosto/alongado.svg' },
            { valor: 'diamante', rotulo: 'Diamante', alt: 'Diamante — ilustração da escala', imagem: 'formato-rosto/diamante.svg' },
            { valor: 'triangular', rotulo: 'Triangular', alt: 'Triangular — ilustração da escala', imagem: 'formato-rosto/triangular.svg' },
        ],
    },
    {
        slug: 'formato-unhas',
        nome: 'Formatos de unhas',
        descricao: 'Desenho da borda livre.',
        colunas: 6,
        opcoes: [
            { valor: 'quadrada', rotulo: 'Quadrada', alt: 'Quadrada — ilustração da escala', imagem: 'formato-unhas/quadrada.svg' },
            { valor: 'cantos-arredondados', rotulo: 'Cantos arredondados', alt: 'Cantos arredondados — ilustração da escala', imagem: 'formato-unhas/cantos-arredondados.svg' },
            { valor: 'redonda', rotulo: 'Redonda', alt: 'Redonda — ilustração da escala', imagem: 'formato-unhas/redonda.svg' },
            { valor: 'oval', rotulo: 'Oval', alt: 'Oval — ilustração da escala', imagem: 'formato-unhas/oval.svg' },
            { valor: 'amendoada', rotulo: 'Amendoada', alt: 'Amendoada — ilustração da escala', imagem: 'formato-unhas/amendoada.svg' },
            { valor: 'bailarina', rotulo: 'Bailarina', alt: 'Bailarina — ilustração da escala', imagem: 'formato-unhas/bailarina.svg' },
            { valor: 'stilleto', rotulo: 'Stilleto', alt: 'Stilleto — ilustração da escala', imagem: 'formato-unhas/stilleto.svg' },
            { valor: 'cabeca-de-seta', rotulo: 'Cabeça de seta', alt: 'Cabeça de seta — ilustração da escala', imagem: 'formato-unhas/cabeca-de-seta.svg' },
            { valor: 'pico-de-montanha', rotulo: 'Pico de montanha', alt: 'Pico de montanha — ilustração da escala', imagem: 'formato-unhas/pico-de-montanha.svg' },
            { valor: 'flare', rotulo: 'Flare', alt: 'Flare — ilustração da escala', imagem: 'formato-unhas/flare.svg' },
            { valor: 'batom', rotulo: 'Batom', alt: 'Batom — ilustração da escala', imagem: 'formato-unhas/batom.svg' },
            { valor: 'beiradas', rotulo: 'Beiradas', alt: 'Beiradas — ilustração da escala', imagem: 'formato-unhas/beiradas.svg' },
        ],
    },
    {
        slug: 'somatotipo',
        nome: 'Somatótipo',
        descricao: 'Classificação de William Sheldon.',
        colunas: 3,
        opcoes: [
            { valor: 'ectomorfo', rotulo: 'Ectomorfo', alt: 'Ectomorfo — ilustração da escala', dica: 'Linear, pouca massa', imagem: 'somatotipo/ectomorfo.svg' },
            { valor: 'mesomorfo', rotulo: 'Mesomorfo', alt: 'Mesomorfo — ilustração da escala', dica: 'Muscular', imagem: 'somatotipo/mesomorfo.svg' },
            { valor: 'endomorfo', rotulo: 'Endomorfo', alt: 'Endomorfo — ilustração da escala', dica: 'Arredondado', imagem: 'somatotipo/endomorfo.svg' },
        ],
    },
    {
        slug: 'escala-fitzpatrick',
        nome: 'Escala de Fitzpatrick',
        descricao: 'Classifique sob luz natural, em área não exposta.',
        colunas: 6,
        opcoes: [
            { valor: 'i', rotulo: 'I', alt: 'Fototipo I', dica: 'Sempre queima, nunca bronzeia', cor: '#F5D5C4' },
            { valor: 'ii', rotulo: 'II', alt: 'Fototipo II', dica: 'Queima fácil, bronzeia pouco', cor: '#EBC1A6' },
            { valor: 'iii', rotulo: 'III', alt: 'Fototipo III', dica: 'Queima moderado, bronzeia gradual', cor: '#D9A57C' },
            { valor: 'iv', rotulo: 'IV', alt: 'Fototipo IV', dica: 'Queima pouco, bronzeia bem', cor: '#BE8256' },
            { valor: 'v', rotulo: 'V', alt: 'Fototipo V', dica: 'Raramente queima, bronzeia muito', cor: '#8D5A36' },
            { valor: 'vi', rotulo: 'VI', alt: 'Fototipo VI', dica: 'Nunca queima, muito pigmentada', cor: '#4B2E1E' },
        ],
    },
    {
        slug: 'escala-cor-urina',
        nome: 'Escala de cor de urina',
        descricao: 'Compare sob luz branca, em recipiente transparente.',
        colunas: 8,
        opcoes: [
            { valor: '1', rotulo: '1', alt: 'Tom 1 da escala', dica: 'Hidratação adequada', cor: '#F7F3D8' },
            { valor: '2', rotulo: '2', alt: 'Tom 2 da escala', cor: '#F3EDB8' },
            { valor: '3', rotulo: '3', alt: 'Tom 3 da escala', cor: '#EFE393' },
            { valor: '4', rotulo: '4', alt: 'Tom 4 da escala', cor: '#E9D66E' },
            { valor: '5', rotulo: '5', alt: 'Tom 5 da escala', dica: 'Desidratação leve', cor: '#DFC24A' },
            { valor: '6', rotulo: '6', alt: 'Tom 6 da escala', cor: '#CFA62F' },
            { valor: '7', rotulo: '7', alt: 'Tom 7 da escala', dica: 'Desidratação importante', cor: '#B8861F' },
            { valor: '8', rotulo: '8', alt: 'Tom 8 da escala', dica: 'Desidratação grave', cor: '#8E5E14' },
        ],
    },
    {
        slug: 'percepcao-imagem',
        nome: 'Escala da percepção da imagem pessoal',
        descricao: 'A distância entre a percebida e a desejada é o dado clínico, não cada resposta isolada.',
        colunas: 9,
        opcoes: [
            { valor: '1', rotulo: '1', alt: 'Silhueta 1 de 9', imagem: 'percepcao-imagem/feminino/1.svg' },
            { valor: '2', rotulo: '2', alt: 'Silhueta 2 de 9', imagem: 'percepcao-imagem/feminino/2.svg' },
            { valor: '3', rotulo: '3', alt: 'Silhueta 3 de 9', imagem: 'percepcao-imagem/feminino/3.svg' },
            { valor: '4', rotulo: '4', alt: 'Silhueta 4 de 9', imagem: 'percepcao-imagem/feminino/4.svg' },
            { valor: '5', rotulo: '5', alt: 'Silhueta 5 de 9', imagem: 'percepcao-imagem/feminino/5.svg' },
            { valor: '6', rotulo: '6', alt: 'Silhueta 6 de 9', imagem: 'percepcao-imagem/feminino/6.svg' },
            { valor: '7', rotulo: '7', alt: 'Silhueta 7 de 9', imagem: 'percepcao-imagem/feminino/7.svg' },
            { valor: '8', rotulo: '8', alt: 'Silhueta 8 de 9', imagem: 'percepcao-imagem/feminino/8.svg' },
            { valor: '9', rotulo: '9', alt: 'Silhueta 9 de 9', imagem: 'percepcao-imagem/feminino/9.svg' },
        ],
    },
    {
        slug: 'rosacea',
        nome: 'Subtipos e grau de rosácea',
        descricao: 'Os subtipos coexistem — marque todos os presentes e classifique cada um.',
        colunas: 4,
        opcoes: [
            { valor: 'subtipo-1', rotulo: 'Subtipo 1', alt: 'Rosácea subtipo 1', dica: 'Eritematotelangiectásica', imagem: 'rosacea/subtipo-1.png' },
            { valor: 'subtipo-2', rotulo: 'Subtipo 2', alt: 'Rosácea subtipo 2', dica: 'Papulopustulosa', imagem: 'rosacea/subtipo-2.png' },
            { valor: 'subtipo-3', rotulo: 'Subtipo 3', alt: 'Rosácea subtipo 3', dica: 'Fimatosa', imagem: 'rosacea/subtipo-3.png' },
            { valor: 'subtipo-4', rotulo: 'Subtipo 4', alt: 'Rosácea subtipo 4', dica: 'Ocular', imagem: 'rosacea/subtipo-4.png' },
        ],
    },
];

export function escalaPorSlug(slug: string | undefined): EscalaIlustrada | undefined {
    return ESCALAS_ILUSTRADAS.find((e) => e.slug === slug);
}

/** O caminho servido da ilustração. Vazio quando a opção ainda não tem arte. */
export function arteDaOpcao(opcao: OpcaoDaEscala): string {
    return opcao.imagem ? urlDaArteClinica(`escalas/${opcao.imagem}`) : '';
}
